import { useEffect, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import ReactFlow, {
  Background, Controls, MiniMap, Handle, Position,
  useNodesState, useEdgesState, addEdge, Connection, Edge, Node,
  MarkerType, ReactFlowProvider,
} from 'reactflow'
import 'reactflow/dist/style.css'
import {
  ArrowLeft, Save, Plus, Workflow, Play, Square, GitBranch, Circle,
  PanelRight, Trash2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface NodeData {
  codigo: string
  nome: string
  tipo: 'inicio' | 'tarefa' | 'decisao' | 'fim'
  sla_horas?: number
  atribuicao_tipo?: string
  atribuicao_valor?: string
  form_schema?: any
}

const TIPOS: Record<string, { label: string; cor: string; Ic: any; bg: string }> = {
  inicio:  { label: 'Início',   cor: 'border-emerald-400', Ic: Play,      bg: 'bg-emerald-50'  },
  tarefa:  { label: 'Tarefa',   cor: 'border-blue-400',    Ic: Workflow,  bg: 'bg-blue-50'     },
  decisao: { label: 'Decisão',  cor: 'border-amber-400',   Ic: GitBranch, bg: 'bg-amber-50'    },
  fim:     { label: 'Fim',      cor: 'border-ink-400',     Ic: Square,    bg: 'bg-ink-50'      },
}

function CustomNode({ data, selected }: { data: NodeData; selected: boolean }) {
  const meta = TIPOS[data.tipo] || TIPOS.tarefa
  const Ic = meta.Ic
  return (
    <div className={`rounded-lg border-2 ${meta.cor} ${meta.bg} px-3 py-2 min-w-[160px] shadow-sm ${selected ? 'ring-2 ring-brand-400' : ''}`}>
      {data.tipo !== 'inicio' && <Handle type="target" position={Position.Left} />}
      <div className="flex items-center gap-2">
        <Ic className="w-4 h-4" />
        <div className="flex-1">
          <div className="text-[10px] uppercase font-medium text-ink-500">{meta.label}</div>
          <div className="font-semibold text-sm">{data.nome}</div>
          {data.sla_horas && <div className="text-[10px] text-ink-500">SLA {data.sla_horas}h</div>}
        </div>
      </div>
      {data.tipo !== 'fim' && <Handle type="source" position={Position.Right} />}
    </div>
  )
}

const nodeTypes = { custom: CustomNode }

function DesignerInner() {
  const { id } = useParams()
  const nav = useNavigate()
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [selected, setSelected] = useState<string | null>(null)

  const { data, isLoading, refetch } = useQuery<any>({
    queryKey: ['bpm-designer', id],
    queryFn: async () => (await api.get(`/bpm/processos/${id}/designer`)).data,
    enabled: !!id,
  })

  useEffect(() => {
    if (!data) return
    const rfNodes: Node[] = data.nodes.map((n: any) => ({
      id: n.codigo,
      type: 'custom',
      position: { x: n.posicao_x || 100, y: n.posicao_y || 100 },
      data: {
        codigo: n.codigo, nome: n.nome, tipo: n.tipo,
        sla_horas: n.sla_horas, atribuicao_tipo: n.atribuicao_tipo,
        atribuicao_valor: n.atribuicao_valor, form_schema: n.form_schema,
      },
    }))

    // Se tem arestas salvas, usa; senão gera a partir de proximo_node_codigo
    let rfEdges: Edge[] = []
    if (data.arestas?.length > 0) {
      rfEdges = data.arestas.map((a: any) => ({
        id: `e-${a.origem_codigo}-${a.destino_codigo}-${a.id}`,
        source: a.origem_codigo,
        target: a.destino_codigo,
        label: a.label || a.condicao || undefined,
        markerEnd: { type: MarkerType.ArrowClosed },
      }))
    } else {
      rfEdges = data.nodes
        .filter((n: any) => n.proximo_node_codigo)
        .map((n: any) => ({
          id: `e-${n.codigo}-${n.proximo_node_codigo}`,
          source: n.codigo, target: n.proximo_node_codigo,
          markerEnd: { type: MarkerType.ArrowClosed },
        }))
    }

    setNodes(rfNodes)
    setEdges(rfEdges)
  }, [data, setNodes, setEdges])

  const onConnect = useCallback((conn: Connection) => {
    setEdges(eds => addEdge({ ...conn, markerEnd: { type: MarkerType.ArrowClosed } }, eds))
  }, [setEdges])

  const salvar = useMutation({
    mutationFn: async () => {
      const payload = {
        nodes: nodes.map(n => ({
          codigo: n.id,
          posicao_x: Math.round(n.position.x),
          posicao_y: Math.round(n.position.y),
          nome: (n.data as NodeData).nome,
        })),
        arestas: edges.map((e, i) => ({
          origem_codigo: e.source,
          destino_codigo: e.target,
          label: typeof e.label === 'string' ? e.label : undefined,
          ordem: i,
        })),
      }
      return (await api.put(`/bpm/processos/${id}/designer`, payload)).data
    },
    onSuccess: () => { toast.success('Fluxo salvo'); refetch() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  function addTarefa() {
    const codigo = `tarefa_${Date.now().toString(36).slice(-4)}`
    setNodes(ns => [...ns, {
      id: codigo, type: 'custom',
      position: { x: 200 + ns.length * 50, y: 200 + ns.length * 30 },
      data: { codigo, nome: 'Nova tarefa', tipo: 'tarefa' },
    }])
  }

  function addDecisao() {
    const codigo = `decisao_${Date.now().toString(36).slice(-4)}`
    setNodes(ns => [...ns, {
      id: codigo, type: 'custom',
      position: { x: 200 + ns.length * 50, y: 200 + ns.length * 30 },
      data: { codigo, nome: 'Decisão', tipo: 'decisao' },
    }])
  }

  function removerSelecionado() {
    if (!selected) return
    const n = nodes.find(x => x.id === selected)
    if (!n) return
    if ((n.data as NodeData).tipo === 'inicio' || (n.data as NodeData).tipo === 'fim') {
      toast.error('Não pode remover início/fim')
      return
    }
    setNodes(ns => ns.filter(x => x.id !== selected))
    setEdges(es => es.filter(e => e.source !== selected && e.target !== selected))
    setSelected(null)
  }

  if (isLoading) return <div className="p-6">Carregando designer...</div>

  const nodeSelecionado = nodes.find(n => n.id === selected)
  const dataSelecionada = nodeSelecionado?.data as NodeData | undefined

  function atualizarSelecionado(changes: Partial<NodeData>) {
    if (!selected) return
    setNodes(ns => ns.map(n => n.id === selected
      ? { ...n, data: { ...(n.data as NodeData), ...changes } }
      : n))
  }

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)]">
      <div className="flex items-center gap-3 p-4 border-b border-ink-200 bg-white">
        <button className="btn-ghost" onClick={() => nav(`/bpm/processos/${id}/editar`)}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Workflow className="w-5 h-5" /> Designer — {data?.processo?.nome}
          </h1>
          <p className="text-xs text-ink-500">Arraste pra mover, puxe da bolinha direita pra conectar</p>
        </div>
        <button onClick={addTarefa} className="btn-secondary !py-1.5 text-xs">
          <Plus className="w-3 h-3" /> Tarefa
        </button>
        <button onClick={addDecisao} className="btn-secondary !py-1.5 text-xs">
          <GitBranch className="w-3 h-3" /> Decisão
        </button>
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending} className="btn-primary">
          <Save className="w-4 h-4" /> {salvar.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <div className="flex-1 flex">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSelected(n.id)}
            onPaneClick={() => setSelected(null)}
            nodeTypes={nodeTypes}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={(n) => {
              const t = (n.data as NodeData).tipo
              return t === 'inicio' ? '#34d399' : t === 'fim' ? '#94a3b8' : t === 'decisao' ? '#fbbf24' : '#60a5fa'
            }} />
          </ReactFlow>
        </div>

        {dataSelecionada && (
          <div className="w-80 border-l border-ink-200 bg-white p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <PanelRight className="w-4 h-4" /> Propriedades
              </h3>
              {dataSelecionada.tipo !== 'inicio' && dataSelecionada.tipo !== 'fim' && (
                <button onClick={removerSelecionado} className="btn-ghost !p-1 text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="label">Código</label>
                <input className="input font-mono" value={dataSelecionada.codigo} readOnly disabled />
              </div>
              <div>
                <label className="label">Nome</label>
                <input className="input" value={dataSelecionada.nome}
                  onChange={e => atualizarSelecionado({ nome: e.target.value })} />
              </div>
              <div>
                <label className="label">Tipo</label>
                <div className="input !bg-ink-50">{TIPOS[dataSelecionada.tipo]?.label}</div>
              </div>
              <p className="text-xs text-ink-500">
                SLA, atribuição e formulário são editados no editor de processo.
                Aqui é só o fluxo visual.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function BPMDesignerPage() {
  return (
    <ReactFlowProvider>
      <DesignerInner />
    </ReactFlowProvider>
  )
}
